// src/components/manuscripts/ManuscriptCalendar.jsx
//
// The right-hand rail of the Manuscripts tab, per the prototype: a month grid
// with today marked, and a "Today's focus" list underneath.
//
// The prototype's grid is a static picture of September 2026 (and is missing
// the 6th and the 13th, so its weeks do not line up); this one is a real
// calendar built from the Date the browser is on. Nothing is plotted ON the
// grid yet -- see the ticket handover: milestone due dates exist per manuscript
// but only behind one /goals call each, which is a request per card.
import React, { useMemo, useState } from 'react';
import { IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import '../../styles/ManuscriptWorkspace.css';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * The 42 cells of a month grid: trailing days of the previous month, the month
 * itself, then enough of the next to fill six whole weeks.
 *
 * Six weeks always, not "as many as it takes" -- a five-week month next to a
 * six-week one makes the whole rail jump height when you page through it.
 */
export function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const lead = first.getDay();
  const start = new Date(year, month, 1 - lead);

  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { date: d, inMonth: d.getMonth() === month };
  });
}

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export default function ManuscriptCalendar({ focus = [], onSelectManuscript }) {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const cells = useMemo(
    () => monthGrid(cursor.getFullYear(), cursor.getMonth()),
    [cursor]
  );

  const shift = (months) =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + months, 1));

  const monthLabel = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <aside className="rv-manuscripts__calendar" aria-label="Calendar and today's focus">
      <div className="rv-calendar__head">
        <span>{monthLabel}</span>
        <div className="rv-calendar__nav">
          <IconButton size="small" aria-label="Previous month" onClick={() => shift(-1)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label="Next month" onClick={() => shift(1)}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className="rv-calendar__grid" role="grid" aria-label={monthLabel}>
        {DAY_NAMES.map((d) => (
          <div key={d} className="rv-calendar__day-name" role="columnheader">
            {d}
          </div>
        ))}
        {cells.map(({ date, inMonth }) => {
          const isToday = sameDay(date, today);
          return (
            <div
              key={date.toISOString()}
              role="gridcell"
              aria-current={isToday ? 'date' : undefined}
              className={[
                'rv-calendar__date',
                inMonth ? '' : 'rv-calendar__date--muted',
                isToday ? 'rv-calendar__date--today' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {date.getDate()}
            </div>
          );
        })}
      </div>

      <div className="rv-calendar__agenda-title">Today&rsquo;s focus</div>

      {focus.length === 0 ? (
        <span className="rv-calendar__agenda-empty">
          Nothing on the go. Start a manuscript and it will show up here.
        </span>
      ) : (
        focus.map((m) => (
          <button
            type="button"
            key={m._id}
            className="rv-calendar__agenda-item"
            data-testid="calendar-focus-item"
            onClick={() => onSelectManuscript?.(m)}
          >
            <span>{m.title || 'Untitled'}</span>
            <span className="rv-calendar__agenda-value">
              {m.taskProgress ? `${m.taskProgress.percent}%` : '—'}
            </span>
          </button>
        ))
      )}
    </aside>
  );
}
